import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plane, Ship, Truck, Calculator, ArrowRight, CheckCircle2 } from "lucide-react";

const MODE_OPTS = [
  { v: "air", label: "Air", Icon: Plane },
  { v: "sea", label: "Sea", Icon: Ship },
  { v: "road", label: "Road", Icon: Truck },
];

// Logged-in customers request a quote from inside their account — same staffed
// flow as the public form, but their account email is prefilled.
export default function RequestQuote() {
  const { user } = useAuth();
  const [f, setF] = useState({
    origin: "", destination: "", mode: "air", weight_kg: 100,
    length_cm: 60, width_cm: 40, height_cm: 40, pieces: 1,
    cargo_type: "general", sea_container: "lcl",
  });
  const [email, setEmail] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter an email so we can send the quote"); return; }
    setSending(true);
    try {
      await api.post("/v1/quote-request", {
        ...f,
        weight_kg: Number(f.weight_kg) || 0,
        length_cm: Number(f.length_cm) || 0,
        width_cm: Number(f.width_cm) || 0,
        height_cm: Number(f.height_cm) || 0,
        pieces: Math.max(1, Number(f.pieces) || 1),
        email: email.trim(), website: "",
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit your request. Please try again.");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <PortalLayout section="customer">
        <div className="vtx-eyebrow">/ Request a quote</div>
        <div className="vtx-card p-8 bg-white text-center max-w-lg mx-auto mt-8" data-testid="quote-request-done">
          <CheckCircle2 size={44} className="mx-auto text-[#166534]" />
          <h2 className="mt-4 text-2xl font-black text-[#0E2F76] tracking-tight">Request received</h2>
          <p className="mt-2 text-[#475569]">Our team is preparing your quote and will email it to <span className="font-semibold text-[#0E2F76]">{email}</span> shortly.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <button className="vtx-btn-primary" onClick={() => setDone(false)} data-testid="quote-new-request">Request another</button>
            <Link className="vtx-btn-ghost" to="/app">Back to dashboard</Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout section="customer">
      <div className="vtx-eyebrow">/ Request a quote</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Request a freight quote</h1>
      <p className="text-sm text-[#475569] mt-1 max-w-2xl">Tell us about your shipment. Our team prices it against live lane rates and emails you a quote — usually within one business day.</p>

      <form onSubmit={submit} className="vtx-card p-6 md:p-8 bg-white shadow-sm mt-6 max-w-3xl" data-testid="quote-form">
        <div className="flex items-center gap-2 mb-6"><Calculator size={18} className="text-[#0E2F76]" /><div className="vtx-eyebrow">Shipment details</div></div>
        <div className="grid grid-cols-3 gap-2 mb-6 max-w-md">
          {MODE_OPTS.map((m) => (
            <button type="button" key={m.v} onClick={() => setF({ ...f, mode: m.v })} data-testid={`mode-${m.v}`}
              className={`p-3 border transition-colors flex flex-col items-center gap-2 ${f.mode === m.v ? "bg-[#0E2F76] text-white border-[#0E2F76]" : "bg-white text-[#0E2F76] border-[#A9C0E0]/60 hover:border-[#0E2F76]"}`} style={{ borderRadius: 2 }}>
              <m.Icon size={20} strokeWidth={2.2} />
              <span className="text-xs font-bold uppercase tracking-wider">{m.label}</span>
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Origin"><input className="vtx-input" placeholder="Mombasa, KE" value={f.origin} onChange={set("origin")} data-testid="q-origin" required /></Field>
          <Field label="Destination"><input className="vtx-input" placeholder="Rotterdam, NL" value={f.destination} onChange={set("destination")} data-testid="q-destination" required /></Field>
          <Field label="Pieces"><input type="number" min="1" className="vtx-input" value={f.pieces} onChange={set("pieces")} data-testid="q-pieces" /></Field>
          <Field label="Weight per piece (kg)"><input type="number" min="0.1" step="0.1" className="vtx-input" value={f.weight_kg} onChange={set("weight_kg")} data-testid="q-weight" required /></Field>
          <Field label="Length (cm)"><input type="number" min="0" className="vtx-input" value={f.length_cm} onChange={set("length_cm")} data-testid="q-length" /></Field>
          <Field label="Width (cm)"><input type="number" min="0" className="vtx-input" value={f.width_cm} onChange={set("width_cm")} data-testid="q-width" /></Field>
          <Field label="Height (cm)"><input type="number" min="0" className="vtx-input" value={f.height_cm} onChange={set("height_cm")} data-testid="q-height" /></Field>
          <Field label="Cargo type">
            <select className="vtx-input" value={f.cargo_type} onChange={set("cargo_type")} data-testid="q-cargo">
              <option value="general">General cargo</option>
              <option value="electronics">Electronics</option>
              <option value="perishable">Perishable</option>
              <option value="dangerous">Dangerous goods</option>
              <option value="reefer">Reefer</option>
            </select>
          </Field>
          {f.mode === "sea" && (
            <Field label="Sea container">
              <select className="vtx-input" value={f.sea_container} onChange={set("sea_container")} data-testid="q-container">
                <option value="lcl">LCL — less than container</option>
                <option value="fcl_20">FCL 20'</option>
                <option value="fcl_40">FCL 40'</option>
              </select>
            </Field>
          )}
          <Field label="Send the quote to">
            <input type="email" className="vtx-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quote-request-email" required />
          </Field>
        </div>
        <button type="submit" disabled={sending} className="vtx-btn-primary mt-6 inline-flex items-center justify-center gap-2" data-testid="quote-submit">
          {sending ? "Sending…" : (<>Request a quote <ArrowRight size={16} /></>)}
        </button>
      </form>
    </PortalLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="vtx-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
